import HttpClient from "./httpClient";

export interface TheoScheduleAction {
  id?: number | string;
  schedule_id?: number | string;
  position?: number;
  action_name: string;
  action_description?: string | null;
  params_json: Record<string, any>;
  config_json?: Record<string, any> | null;
  arg_types_json?: Record<string, any> | null;
  resolved_args?: Record<string, any> | null;
  source_ai_thread_id?: number | string | null;
  source_message_id?: number | string | null;
  source_call_id?: string | null;
  status?: string;
  last_action_run_id?: number | string | null;
  last_error_message?: string | null;
  last_result_json?: Record<string, any> | null;
}

export interface TheoSchedule {
  id?: number | string;
  msp_id?: number;
  title: string;
  description?: string | null;
  scheduled_for: string;
  timezone?: string;
  status?: "scheduled" | "queued" | "running" | "completed" | "failed" | "canceled";
  created_by?: string;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
  error_message?: string | null;
  actions?: TheoScheduleAction[];
  latest_run?: Record<string, any> | null;
}

export interface TheoNotification {
  id: number | string;
  type: string;
  title: string;
  body?: string | null;
  entity_type: string;
  entity_id: number | string;
  metadata_json?: Record<string, any> | null;
  read_at?: string | null;
  created_at?: string;
  unread?: boolean;
}

class ScheduleApi extends HttpClient {
  listSchedules = (query?: {
    limit?: number | string;
    ai_thread_id?: number | string | null;
    ticket_id?: number | string | null;
    status?: string | null;
  }) => {
    const params = Object.fromEntries(
      Object.entries(query || {}).filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== "")
    );
    return this.get("/api/schedules", undefined, params) as Promise<{ schedules: TheoSchedule[] }>;
  };

  getSchedule = (scheduleId: number | string) =>
    this.get(`/api/schedules/${scheduleId}`) as Promise<{ schedule: TheoSchedule }>;

  createSchedule = (payload: Partial<TheoSchedule>) =>
    this.post("/api/schedules", {}, payload) as Promise<{ schedule: TheoSchedule }>;

  updateSchedule = (scheduleId: number | string, payload: Partial<TheoSchedule>) =>
    this.put(`/api/schedules/${scheduleId}`, {}, payload) as Promise<{ schedule: TheoSchedule }>;

  cancelSchedule = (scheduleId: number | string, payload?: { resume_workflow?: boolean }) =>
    this.post(`/api/schedules/${scheduleId}/cancel`, {}, payload || {}) as Promise<{ schedule: TheoSchedule }>;

  scheduleHitlActions = (payload: {
    schedule_id?: number | string | null;
    schedule?: Partial<TheoSchedule> | null;
    ai_thread_id: number | string;
    customer_id?: number | string | null;
    message_id?: number | string | null;
    human_review: Record<string, any>;
  }) =>
    this.post("/api/schedules/hitl", {}, payload) as Promise<{ schedule: TheoSchedule }>;

  scheduleWorkflowStart = (payload: {
    workflow_id: number | string;
    ticket_id: string;
    ticket_title?: string | null;
    ai_thread_id?: number | string | null;
    scheduled_for: string;
    timezone: string;
    step_delay_overrides?: Array<{
      index: number;
      id?: string;
      action?: string;
      schedule_delay_enabled: string;
      schedule_delay_amount?: string;
      schedule_delay_unit?: string;
      schedule_delay_reason?: string;
    }>;
  }) =>
    this.post("/api/schedules/workflow-start", {}, payload) as Promise<{ schedule: TheoSchedule }>;

  listNotifications = () =>
    this.get("/api/notifications") as Promise<{
      notifications: TheoNotification[];
      unread_count: number;
    }>;

  markNotificationRead = (notificationId: number | string) =>
    this.post(`/api/notifications/${notificationId}/read`, {}, {}) as Promise<{ success: boolean }>;

  markAllNotificationsRead = () =>
    this.post("/api/notifications/read-all", {}, {}) as Promise<{ success: boolean }>;

  deleteNotification = (notificationId: number | string) =>
    this.del(`/api/notifications/${notificationId}`) as Promise<{ success: boolean }>;

  deleteAllNotifications = () =>
    this.del("/api/notifications") as Promise<{ success: boolean }>;
}

export default ScheduleApi;
